import { ReactElement } from 'react';

import { supportedLngs } from '@services/i18nService';
import OptimizelyClientService from '@services/optimizelyClientService';
import { captureException } from '@services/sentryService';
import { Environment, Nullable, Locale } from '@types-internal';
import { AdyenEnvironment } from '@types-internal/AdyenTypes';

export const hasText = (input: any) => {
  return input && typeof input === 'string' && input.length;
};

/**
 * Convert a currency from Major Units to Minor Units.
 * <p>For example: From 529.95€ to 52995 cents.
 *
 * @param majorUnits The major units number
 * @return The number in minor units
 */
export const convertMajorUnitsToMinorUnits = (majorUnits: number): number => {
  return Math.round(majorUnits * 100);
};

export const safeStringify = (value: any) => {
  // just in case the value is an object with circular references
  try {
    return JSON.stringify(value);
  } catch (error) {
    captureException({
      error,
    });
    return '';
  }
};

export function objectHasKey(object: object, key: string): key is keyof object {
  return key in object;
}

export function messageOfUnknownError(error: unknown): string {
  const key = 'message';
  if (typeof error === 'undefined') {
    return 'undefined'; // we handle this explicitly because JSON.stringify does not output a string when passed undefined
  } else if (typeof error === 'string') {
    return error; // we already have a string
  } else if (typeof error === 'object' && error !== null && objectHasKey(error, key)) {
    const value = error[key]; // the message field is there, it should be a string but a test cannot hurt
    return typeof value === 'string' ? value : safeStringify(value);
  } else {
    return safeStringify(error);
  }
}

export function defaultString(value: unknown, defaultValue = ''): string {
  return typeof value === 'string' ? value : defaultValue;
}

export function defaultBoolean(value: unknown, defaultValue = false): boolean {
  return typeof value === 'boolean' ? value : defaultValue;
}

export function defaultNullString(value: unknown): Nullable<string> {
  return typeof value === 'string' ? value : null;
}

// require an explicit name (instead of using fn.name) in order to accept anonymous functions, for instance the ones created by useCallback
export function exposeGlobally(name: string, fn: (...args: unknown[]) => unknown): () => void {
  (window as any).Payment = {
    ...((window as any).Payment || {}),
    [name]: fn,
  };
  return () => {
    (window as any).Payment[name] = undefined;
  };
}

export function catchFatalError<Inputs extends unknown[]>(
  onFatalError: (error: unknown) => void,
  fn: (...args: Inputs) => void,
) {
  return (...args: Inputs) => {
    try {
      fn(...args);
    } catch (error: unknown) {
      onFatalError(error);
    }
  };
}

/**
 * <p>Convert the Zoo+ running environment to the appropriate Adyen environment. Zoo+ is running on the env such as
 * "dev" or "prod", while Adyen expects the environment to be "test" or "live".
 *
 * <p>At the moment, we have this env mapping:
 *   - "prod" (z+) = "live" (Adyen)
 *   - "dev"  (z+) = "test" (Adyen)
 *   - Others env from z+ = "test" (Adyen)
 *
 * @param runningEnv The Zoo+ running environment
 */
export const convertRunningEnvToAdyenEnv = (runningEnv: Environment): AdyenEnvironment => {
  return runningEnv === 'prod' ? 'live' : 'test';
};

export const flipObject = (obj: any) => {
  return Object.fromEntries(Object.entries(obj).map((a) => a.reverse()));
};

export const range = (n: number) => {
  return [...Array(n).keys()];
};

/**
 * Function to return the current month and the last two digits of the current year.
 * The function converts the month to a 1-indexed format (1 for January, 12 for December)
 * and truncates the year to its last two digits for compactness.
 *
 * @returns {Object} An object containing:
 *   - `curMonth` (number): The current month in 1-indexed format.
 *   - `curYear` (number): The last two digits of the current year.
 *
 * Example:
 *   If the current date is December 2, 2024:
 *   getMonthDate() will return: { curMonth: 12, curYear: 24 }
 *
 */
export const getMonthDate = () => {
  const now = new Date();
  // Extracting current month and year
  const curMonth = now.getMonth() + 1; // Add 1 to make month 1-indexed (1 for January, 12 for December)
  const curYear = now.getFullYear() % 100; // Extract last two digits of the year

  return { curMonth, curYear };
};

/**
 * Function to return capitalized string
 * @param str string to be capitalized
 * @returns capitalized string
 */

export const capitalize = (str: Nullable<string>) => {
  if (!str) return null;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * <p>Require the input value not to be "null" or "undefined". If the given value
 * is "null" or "undefined", an error will be thrown.
 *
 * <i>Note: This function will not check for other falsy situation such as an empty string,
 * a 0 number, an empty object, etc...
 *
 * @param value The value to check
 * @param fieldName The field name that we are checking, used to generate a meaningful
 * error message. If this param is not specify, a general error message will be used
 */
export const required = <T,>(value: any, fieldName?: string): T => {
  // (value == null) covered the undefined value
  if (value == null) {
    if (fieldName) {
      throw new Error(`Required check failed: [${fieldName}] is null or undefined`);
    } else {
      throw new Error(`Required check failed: A value is null or undefined`);
    }
  }
  return value;
};

/**
 * Function to return a locale from a country param
 *
 * @param country string of a supported country code
 * @returns locale string based on the country or default one if country code is not supported
 */
export const getLocaleFromCountry = (country: string) => {
  const lngs = supportedLngs.find((lng) => lng.split('-')?.[1] === country) || 'de-DE';
  return lngs;
};

export async function checkFeatureForUser(
  featureKey: string,
  attributes: Record<string, number>,
  optimizelyId: string,
): Promise<boolean> {
  const optimizely = new OptimizelyClientService();
  optimizely.initialize(optimizelyId);
  return await optimizely.isFeatureEnabled(featureKey, attributes);
}

/**
 * <p>Handles the "keydown" event on a specified element. If the key pressed is either the
 * "Enter" or "Space" key, the provided callback function will be invoked, and default event
 * behaviors (like form submission) will be prevented.</p>
 *
 * <i>Note: The function only triggers the callback for the "Enter" and "Space" keys. For any other key press,
 * no action will be taken.</i>
 *
 * @param event The keyboard event that was triggered by the key press.
 * @param callback A function to be called when the "Enter" or "Space" key is pressed. The callback will receive
 *                 the keyboard event as its argument.
 */
export const handleKeyDown = (
  event: React.KeyboardEvent<HTMLSpanElement>,
  callback?: React.MouseEventHandler,
) => {
  if (callback && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault();
    event.stopPropagation();
    callback(event as unknown as React.MouseEvent<HTMLSpanElement>);
  }
};

/**
 * Gets a string and returns the environment type.
 * @param env The string env getting passed
 * @returns Supported environment
 */
export const getEnv = (env?: string): Environment => {
  if (!env) {
    return 'prod';
  }
  if (['local', 'dev', 'development', 'develop', 'test', 'release'].includes(env)) {
    return 'dev';
  }

  if (['beta', 'prod', 'production'].includes(env)) {
    return 'prod';
  }

  return 'prod';
};

/**
 * Returns privacy links for Riverty based on locale.
 * @param locale The current locale
 * @returns Privacy links for Riverty
 */
export const getRivertyUrls = (locale: Locale): Record<'tc-url' | 'dp-url', ReactElement<any>> => {
  const rivertyLocaleMap: Partial<Record<Locale, string>> = {
    'nl-NL': 'nl_nl',
    'de-DE': 'de_de',
  };
  const baseUrl = 'https://documents.riverty.com';
  const rivertyDocLocale = rivertyLocaleMap[locale] ?? 'nl_en';
  return {
    'tc-url': (
      <a
        target="_blank"
        rel="noreferrer"
        href={`${baseUrl}/terms_conditions/payment_methods/invoice/${rivertyDocLocale ?? 'nl_en'}/`}
      />
    ),
    'dp-url': (
      <a
        target="_blank"
        rel="noreferrer"
        href={`${baseUrl}/privacy_statement/checkout/${rivertyDocLocale ?? 'nl_en'}/`}
      />
    ),
  };
};
